import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { authService } from '../services/authService';
import type {
  CompleteRegistrationFormData,
  RegistrationUserInfoResponse,
} from '../types/auth.types';

const completeRegistrationSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name is required')
      .max(100, 'First name must be less than 100 characters'),
    middleName: z
      .string()
      .max(100, 'Middle name must be less than 100 characters')
      .optional()
      .or(z.literal('')),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(100, 'Last name must be less than 100 characters'),
    mobile: z
      .string()
      .max(20, 'Mobile must be less than 20 characters')
      .optional()
      .or(z.literal('')),
    referralCode: z
      .string()
      .max(100, 'Referral code must be less than 100 characters')
      .optional()
      .or(z.literal('')),
    password: z
      .string()
      .min(6, 'Password must be at least 6 characters')
      .max(100, 'Password must be less than 100 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength += 25;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^A-Za-z0-9]/.test(password)) strength += 10;
  return Math.min(strength, 100);
};

const getStrengthColor = (strength: number): string => {
  if (strength < 30) return 'bg-destructive';
  if (strength < 60) return 'bg-yellow-500';
  if (strength < 80) return 'bg-blue-500';
  return 'bg-green-500';
};

const getStrengthLabel = (strength: number): string => {
  if (strength < 30) return 'Weak';
  if (strength < 60) return 'Fair';
  if (strength < 80) return 'Good';
  return 'Strong';
};

const PASSWORD_RULES = [
  'At least 6 characters (required)',
  '8+ characters for stronger password',
  'Include uppercase letter',
  'Include lowercase letter',
  'Include a number',
  'Include a special character',
];

export interface CompleteRegistrationFormProps {
  userInfo: RegistrationUserInfoResponse;
  token: string;
  queryParams: Record<string, string>;
  onSuccess: () => void;
  onError: (message: string) => void;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    return (
      err.response?.data?.message ??
      err.response?.data?.error ??
      err.message ??
      'Something went wrong'
    );
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

export function CompleteRegistrationForm({
  userInfo,
  token,
  onSuccess,
  onError,
}: CompleteRegistrationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CompleteRegistrationFormData>({
    resolver: zodResolver(completeRegistrationSchema),
    defaultValues: {
      firstName: userInfo.firstName ?? '',
      middleName: userInfo.middleName ?? '',
      lastName: userInfo.lastName ?? '',
      mobile: userInfo.mobile ?? '',
      referralCode: userInfo.referralCode ?? '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');
  const passwordStrength = calculatePasswordStrength(password ?? '');

  const onSubmit = async (data: CompleteRegistrationFormData) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await authService.completeRegistration({
        token,
        userId: userInfo.userId,
        password: data.password,
        firstName: data.firstName || undefined,
        middleName: data.middleName?.trim() || undefined,
        lastName: data.lastName || undefined,
        mobile: data.mobile || undefined,
        referralCode: data.referralCode?.trim() || undefined,
      });
      onSuccess();
    } catch (err) {
      const message = getErrorMessage(err);
      setSubmitError(message);
      onError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Complete Registration</CardTitle>
        <CardDescription>
          Set your password and confirm your details to finish setting up your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">Email</Label>
            <Input
              id="userId"
              type="email"
              value={userInfo.userId}
              disabled
              readOnly
              className="bg-muted cursor-not-allowed"
              aria-readonly
            />
            <p className="text-xs text-muted-foreground">
              Your email cannot be changed.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="middleName">Middle Name</Label>
              <Input
                id="middleName"
                type="text"
                placeholder=""
                {...register('middleName')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile (Optional)</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="+1234567890"
              {...register('mobile')}
            />
            {errors.mobile && (
              <p className="text-sm text-destructive">{errors.mobile.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="referralCode">Referral code (Optional)</Label>
            <Input
              id="referralCode"
              type="text"
              placeholder="e.g. REF123"
              autoComplete="off"
              {...register('referralCode')}
            />
            {errors.referralCode && (
              <p className="text-sm text-destructive">{errors.referralCode.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              {...register('password')}
            />
            <div className="flex items-center gap-2 mt-1">
              <Progress
                value={passwordStrength}
                className={`h-2 flex-1 ${getStrengthColor(passwordStrength)}`}
              />
              <span className="text-xs text-muted-foreground w-12 shrink-0">
                {getStrengthLabel(passwordStrength)}
              </span>
            </div>
            <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5 mt-1">
              {PASSWORD_RULES.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInput
              id="confirmPassword"
              placeholder="••••••••"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {submitError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {submitError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            loading={isSubmitting}
            loadingLabel="Completing…"
          >
            Complete Registration
          </Button>

          <div className="text-center text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Login
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
